import { RADIUS } from "@elyxeer/ui";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EventStateChip } from "~/components/events/EventStateChip";
import { useLocale } from "~/i18n";
import type { EventAdmin } from "~/types/events-admin";

const MEMBER_APP_URL =
  (import.meta.env.VITE_MEMBER_APP_URL as string | undefined) ??
  "https://app.elyxeer.com";

/**
 * Where the webinar stands and the one hand-operated lever it has.
 *
 * The state is derived on the server from the schedule, the "Proběhlo" stamp and the
 * recording, so this panel only *shows* it — except for the stamp, which is the editor's
 * to set when a broadcast ends early or has no end time, and to clear when they were
 * wrong. Both are one request each, outside the form: the form saves a draft of the
 * event, ending a broadcast is an act.
 */
export function EventStatusPanel({
  event,
  busy,
  onEnd,
}: {
  event: EventAdmin;
  busy: boolean;
  /** Set or clear the "Proběhlo" stamp. */
  onEnd: (endedAt: string | null) => void;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const [copied, setCopied] = useState(false);

  const publicUrl = `${MEMBER_APP_URL.replace(/\/$/, "")}/live/${event.slug}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
    } catch {
      // The field below is selectable; the button is a convenience.
    }
  };

  return (
    <Paper
      component="section"
      variant="outlined"
      data-testid="event.status"
      sx={{
        padding: 3,
        borderRadius: `${RADIUS.lg}px`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="H3" sx={{ color: "text.primary" }}>
          {t("eventEditor.status.title")}
        </Typography>
        <EventStateChip state={event.state} />
      </Box>
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t("eventEditor.status.hint")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("eventEditor.status.registrations")}
          </Typography>
          <Typography
            variant="H2"
            sx={{ color: "text.primary" }}
            data-testid="event.status-registrations"
          >
            {event.registration_count}
          </Typography>
          {event.pending_registration_count > 0 ? (
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("eventEditor.status.pending", {
                count: event.pending_registration_count,
              })}
            </Typography>
          ) : null}
        </Box>
        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("eventEditor.status.questions")}
          </Typography>
          <Typography
            variant="H2"
            sx={{ color: "text.primary" }}
            data-testid="event.status-questions"
          >
            {event.question_count}
          </Typography>
          {event.unanswered_question_count > 0 ? (
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("eventEditor.status.unanswered", {
                count: event.unanswered_question_count,
              })}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {event.recording_slug ? (
        event.recording_title ? (
          <Typography variant="T3" sx={{ color: "text.secondary" }}>
            {t("eventEditor.status.recording", {
              title: event.recording_title,
            })}
          </Typography>
        ) : (
          <Alert severity="warning" variant="outlined">
            {t("eventEditor.status.recordingMissing")}
          </Alert>
        )
      ) : null}

      {event.ended_at ? (
        <Typography
          variant="T3"
          sx={{ color: "text.secondary" }}
          data-testid="event.status-ended-at"
        >
          {t("eventEditor.status.endedAt", {
            date: formatDate(event.ended_at, {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            }),
          })}
        </Typography>
      ) : null}

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {event.ended_at ? (
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => onEnd(null)}
            data-testid="event.reopen"
          >
            {t("eventEditor.status.reopen")}
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="small"
            disabled={busy || event.state === "recording"}
            onClick={() => onEnd(new Date().toISOString())}
            data-testid="event.mark-ended"
          >
            {t("eventEditor.status.markEnded")}
          </Button>
        )}
      </Box>

      {event.registration_enabled ? (
        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("eventEditor.status.publicPage")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="T3"
              component="code"
              data-testid="event.public-url"
              sx={{
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                userSelect: "all",
              }}
            >
              {publicUrl}
            </Typography>
            <IconButton
              size="small"
              aria-label={t("eventEditor.status.copied")}
              onClick={() => void copy()}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      ) : null}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t("eventEditor.status.copied")}
      />
    </Paper>
  );
}
